import type { OrganizationEntry } from '@obras/shared';
import { auditActionLabel, formatDateTime } from '@obras/shared';
import { Search } from 'lucide-react';
import { useEffect, useState } from 'react';

import { SearchInput } from '../../components/ui/Field.js';
import { EmptyState, ErrorState, Skeleton } from '../../components/ui/Feedback.js';
import { Modal } from '../../components/ui/Modal.js';
import { useOrganizationAudit } from './api.js';

/**
 * A trilha de uma organização, lida pela plataforma.
 *
 * É a mesma trilha que o órgão vê por dentro, sem filtro de ação nem de período:
 * quem olha daqui está conferindo o que aconteceu num cliente, não auditando o
 * próprio trabalho. Para a investigação detalhada existe a tela de dentro.
 *
 * O acesso fica registrado na trilha da própria organização visitada — abrir
 * esta janela é um ato, e atos de quem opera o produto sobre o ambiente de um
 * cliente são exatamente o que uma trilha existe para mostrar.
 */
export function OrganizationAuditModal({
  organization,
  onClose,
}: {
  organization: OrganizationEntry | null;
  onClose: () => void;
}) {
  const [busca, setBusca] = useState('');
  const [termo, setTermo] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setTermo(busca), 300);
    return () => clearTimeout(timer);
  }, [busca]);

  const { data, isPending, isError, error } = useOrganizationAudit(organization?.id ?? null, {
    search: termo || undefined,
    perPage: 40,
  });

  return (
    <Modal
      open={organization !== null}
      onClose={onClose}
      title="Trilha de auditoria"
      description={organization?.name ?? ''}
      size="lg"
    >
      <div className="flex flex-col gap-4">
        <div className="relative">
          <SearchInput
            value={busca}
            onChange={(event) => setBusca(event.target.value)}
            onClear={() => setBusca('')}
            placeholder="Buscar por resumo, IP ou identificador"
            aria-label="Buscar na trilha"
          />
        </div>

        {isError ? (
          <ErrorState message={error instanceof Error ? error.message : 'Erro desconhecido.'} />
        ) : isPending ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} className="h-11" />
            ))}
          </div>
        ) : data.items.length === 0 ? (
          <EmptyState
            icon={Search}
            title="Nada na trilha"
            description={
              termo
                ? 'Nenhum registro para este termo.'
                : 'Esta organização ainda não tem registro.'
            }
          />
        ) : (
          <>
            <ul className="divide-y divide-line">
              {data.items.map((item) => (
                <li key={item.id} className="flex flex-col gap-0.5 py-2.5">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-[13px] font-medium">{auditActionLabel(item.action)}</span>
                    <span className="shrink-0 font-mono text-[11px] text-ink-3">
                      {formatDateTime(item.createdAt)}
                    </span>
                  </div>
                  <p className="text-[13px] text-ink-3">{item.summary}</p>
                  <p className="text-[11px] text-ink-3/80">
                    {item.user?.name ?? 'sistema'}
                    {item.ipAddress ? ` · ${item.ipAddress}` : ''}
                  </p>
                </li>
              ))}
            </ul>

            {data.total > data.items.length ? (
              <p className="text-center text-[12px] text-ink-3">
                {data.items.length} de {data.total} registros. Use a busca para estreitar.
              </p>
            ) : null}
          </>
        )}
      </div>
    </Modal>
  );
}
